import React, { useState, useEffect } from 'react';
import { Info, Power } from 'lucide-react';
import Lottie from 'lottie-react';
import WebStepShell from './WebStepShell';
import DeviceMedia from './DeviceMedia';
import type { WebFlowCtx } from './types';

interface Props {
  ctx: WebFlowCtx;
}

type SubStep = 'main' | 'replug' | 'powerSwitch';

const Step1TurnOn: React.FC<Props> = ({ ctx }) => {
  const [subStep, setSubStep] = useState<SubStep>('main');
  const [replugAnim, setReplugAnim] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (!replugAnim) {
      fetch(`${process.env.PUBLIC_URL}/DeviceSetup/re-plugging.json`)
        .then((res) => res.json())
        .then(setReplugAnim)
        .catch(() => {});
    }
  }, [subStep, replugAnim]);

  /* ── Sub-step: Let's try re-plugging your device ── */
  if (subStep === 'replug') {
    return (
      <WebStepShell
        step="on"
        onHeaderBack={ctx.openExitModal}
        onCancel={ctx.openExitModal}
        secondary={{ label: 'Light is not Green', onClick: () => setSubStep('powerSwitch') }}
        primary={{ label: 'Light is Green', onClick: () => ctx.goTo('wificonf') }}
        media={
          <div className="dcfw-image-source">
            <div className="dcfw-device-wrap">
              {replugAnim ? (
                <Lottie
                  animationData={replugAnim}
                  loop
                  autoplay
                  style={{ width: '100%', height: '100%' }}
                />
              ) : (
                <picture>
                  <source
                    srcSet={`${process.env.PUBLIC_URL}/DeviceSetup/device-mobile.avif`}
                    type="image/avif"
                  />
                  <img
                    srcSet={`${process.env.PUBLIC_URL}/DeviceSetup/device-mobile.webp`}
                    alt="Unplug and replug your SmartEco device"
                    className="dcf-illustration"
                  />
                </picture>
              )}
            </div>
            <div className="dcfw-image-caption">
              <Power size={14} />
              <span>Indicator light-is OFF</span>
            </div>
          </div>
        }
      >
        <p className="dcfw-eyebrow">SETUP PHASE 1</p>
        <h2 className="dcfw-title">Let&apos;s try re-plugging your device</h2>
        <p className="dcf-step-subtitle">
          Unplug your device, wait a few seconds, then plug it back in firmly.
          Watch for the indicator light to turn solid green.
        </p>
      </WebStepShell>
    );
  }

  /* ── Sub-step: Let's try the power switch ── */
  if (subStep === 'powerSwitch') {
    return (
      <WebStepShell
        step="on"
        onHeaderBack={ctx.openExitModal}
        onCancel={ctx.openExitModal}
        secondary={{ label: 'Try Again', onClick: () => setSubStep('replug') }}
        primary={{ label: 'Done', onClick: () => ctx.goTo('wificonf') }}
        media={
          <div className="dcfw-image-source">
            <div className="dcfw-device-wrap">
              <img
                src={`${process.env.PUBLIC_URL}/DeviceSetup/power-switch.webp`}
                alt="Toggle the power switch on your SmartEco device"
                className="dcf-illustration"
              />
            </div>
            <div className="dcfw-image-caption">
              <Power size={14} />
              <span>Indicator light-is OFF</span>
            </div>
          </div>
        }
      >
        <p className="dcfw-eyebrow">SETUP PHASE 1</p>
        <h2 className="dcfw-title">Let&apos;s try the power switch</h2>
        <p className="dcf-step-subtitle">
          Hold the power switch on the back of your device. Turn it OFF, wait
          a moment, then turn it back ON. Watch for the indicator light to
          turn solid green.
        </p>
      </WebStepShell>
    );
  }

  /* ── Main step 1 ── */
  return (
    <WebStepShell
      step="on"
      onHeaderBack={ctx.openExitModal}
      onCancel={ctx.openExitModal}
      secondary={{ label: 'Light is not Green', onClick: () => setSubStep('replug') }}
      primary={{ label: 'Light is Green', onClick: () => ctx.goTo('wificonf') }}
      media={<DeviceMedia indicator="step1" caption="Indicator light-is ON" />}
    >
      <p className="dcfw-eyebrow">SETUP PHASE 1</p>
      <h2 className="dcfw-title">Step 1 of 6: Turn on your SmartEco device</h2>
      <p className="dcf-step-subtitle">
        Check the indicator light. Located at the base of the device, it may take
        up to 5 seconds to turn solid green.
      </p>

      <div className="dcf-banner" style={{ marginTop: 12 }}>
        <Info size={20} color="#EEC94D" className="flex-shrink-0" />
        <div className="dcf-banner-description">
          <span>Waiting for solid green?</span>
          <span>
            Do not proceed until the light is steady. If still blinking after 10
            seconds, unplug and replug the device.
          </span>
        </div>
      </div>
    </WebStepShell>
  );
};

export default Step1TurnOn;
